import { useContext, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import 'yup-phone-lite';
import i18nContext from 'components/i18n-context';
import {
  getErrorMessageForAlert,
  prepareFieldErrors,
  replaceSpaces,
  emailRegExp,
  shouldAutoFocus
} from 'services/utils';
import { createBlurValidator, createNoSpacesChangeHandler } from 'services/formValidation';
import { ROUTE_PATHS } from 'routes/constants';
import Alert from 'uikit/Alert/Alert';
import Button from 'uikit/Button/Button';
import Input from 'uikit/Input/Input';
import Tooltip from 'uikit/Tooltip/Tooltip';
import Loader from 'components/common/Loader';
import AuthPageLink from 'components/common/AuthPageLink';
import AuthMobileApp, { AUTH_MOBILE_APP_VARIANTS } from 'components/common/AuthMobileApp';
import { ReactComponent as AppleLogo } from 'assets/apple-logo.svg';
import { ReactComponent as GoogleLogo } from 'assets/google-logo.svg';
import './Login.scss';
import { cleanPhoneNumber } from '../utils';

const LOGIN_METHODS = {
  EMAIL: 'email',
  PHONE: 'phone'
};

const logoPath = process.env.REACT_APP_LOGO_PATH;

const Login = ({
  phone,
  setPhone,
  setUsername,
  setPassword,
  sendAuthorizationData,
  error: storeAPIError,
  isLoading
}) => {
  const i18n = useContext(i18nContext);
  const [activeMethod, setActiveMethod] = useState(LOGIN_METHODS.PHONE);
  const [countryCode, setCountryCode] = useState(process.env.REACT_APP_DEFAULT_COUNTRY_CODE);
  // Suppresses a stale storeAPIError (e.g. a failed Email attempt) from bleeding onto the fields
  // of the tab the user just switched to. Local-only — doesn't touch authStore.error itself, so
  // the store's own error state (and whatever else reads it) is unaffected.
  const [isStoreErrorDismissed, setIsStoreErrorDismissed] = useState(false);

  useEffect(() => {
    setIsStoreErrorDismissed(false);
  }, [storeAPIError]);

  const visibleStoreError = isStoreErrorDismissed ? null : storeAPIError;

  const isEmailMethod = activeMethod === LOGIN_METHODS.EMAIL;

  const form = useFormik({
    validateOnChange: false,
    enableReinitialize: false,
    initialValues: {
      phone: phone,
      email: '',
      password: ''
    },
    initialStatus: { password: true, username: true },
    validationSchema: Yup.object(
      isEmailMethod
        ? {
            email: Yup.string()
              .nullable(true)
              .required(i18n.getMessage('login.error.emptyEmail'))
              .max(50, i18n.getMessage('error.emailSize'))
              .trim()
              .matches(emailRegExp, { message: i18n.getMessage('login.error.invalidEmail') })
              .email(i18n.getMessage('login.error.invalidEmail')),
            password: Yup.string()
              .nullable(true)
              .required(i18n.getMessage('login.error.emptyFiled', { field: 'password' }))
              .min(7, i18n.getMessage('error.PASSWORD_REQUIREMENTS'))
              .max(30, i18n.getMessage('error.fieldSize', { amount: '30' }))
          }
        : {
            phone: Yup.string()
              .nullable(true)
              .required(i18n.getMessage('login.error.emptyPhone'))
              .phone(countryCode.toUpperCase(), i18n.getMessage('error.fieldError.phone.Phone')),
            password: Yup.string()
              .nullable(true)
              .required(i18n.getMessage('login.error.emptyFiled', { field: 'password' }))
              .min(7, i18n.getMessage('error.PASSWORD_REQUIREMENTS'))
              .max(30, i18n.getMessage('error.fieldSize', { amount: '30' }))
          }
    ),
    onSubmit: async (values) => {
      // Belt-and-suspenders per CLAUDE.md: the Button's isDisabled can lag one render behind a very
      // fast double-click/Enter-repeat, and nothing in authStore.sendAuthorizationData() guards
      // against a second concurrent call — so guard here too.
      if (isLoading) return;

      if (isEmailMethod) {
        const email = replaceSpaces(values.email);
        setUsername(email);
        // Prevent a stale/default phone (leftover from an earlier Phone-tab attempt, or just the
        // default dial code) from leaking into authStore.phone — sendAuthorizationData() mirrors
        // values.phone into authStore.phone unconditionally, and that value is what the SMS 2FA
        // screen displays, regardless of which tab was actually used to log in.
        setPhone('');
      } else {
        const cleanedPhone = cleanPhoneNumber(values.phone);
        setPhone(cleanedPhone);
        setUsername(cleanedPhone);
      }
      setPassword(replaceSpaces(values.password));
      await sendAuthorizationData();
    }
  });

  const { values, errors, handleSubmit, handleChange, submitCount, setFieldValue, setErrors, setFormikState } = form;

  const validateOnBlur = createBlurValidator(form);
  const handleEmailChange = createNoSpacesChangeHandler(form);

  const switchMethod = (method) => {
    if (method === activeMethod || isLoading) return;
    setErrors({});
    // Both tabs share this one Formik instance/submitCount. Clearing `errors` above already stops
    // the tab being switched to from showing the OTHER tab's leftover validation errors, but
    // without also resetting submitCount, its (still empty) fields' `initialStatus` prop stays
    // truthy — Input.js's "hit" (green, successfully-validated) styling would show for a field
    // that was never actually validated on this tab, just because it also has no error right now.
    // Values are reset too — only one identifier/password pair should be "active" at a time, so
    // switching tabs shouldn't let a value typed on one carry over if the user switches back.
    setFormikState((prev) => ({ ...prev, values: { phone: '', email: '', password: '' }, submitCount: 0 }));
    setIsStoreErrorDismissed(true);
    setActiveMethod(method);
  };

  return (
    <section className={'login-form'}>
      <a
        href={process.env.REACT_APP_LINK_HOME_PAGE}
        target={'_blank'}
        rel={'noreferrer'}
        className={'login-form__mobile-logo'}
      >
        <img src={logoPath} alt={'Transferra'} />
      </a>

      <div className={'login-form__body'}>
        <div className={'login-form__heading'}>
          <h1 className={'login-form__title'}>{i18n.getMessage('login.pageTitle')}</h1>
        </div>

        <Alert type={'warning'} message={visibleStoreError && getErrorMessageForAlert(i18n, visibleStoreError)} />

        <div className={'login-form__tabs'} role={'tablist'}>
          <button
            type={'button'}
            role={'tab'}
            aria-selected={isEmailMethod}
            className={`login-form__tab ${isEmailMethod ? 'login-form__tab--active' : ''}`}
            onClick={() => switchMethod(LOGIN_METHODS.EMAIL)}
          >
            {i18n.getMessage('login.tab.email')}
          </button>
          <button
            type={'button'}
            role={'tab'}
            aria-selected={!isEmailMethod}
            className={`login-form__tab ${!isEmailMethod ? 'login-form__tab--active' : ''}`}
            onClick={() => switchMethod(LOGIN_METHODS.PHONE)}
          >
            {i18n.getMessage('login.tab.phone')}
          </button>
        </div>

        {/*
          autoComplete="off" on the form is largely advisory for Chrome's credential manager on a
          recognized login form — see the per-field autoComplete="new-password" below for the part
          that actually works.
        */}
        <form className={'login-form__form'} id={'login'} onSubmit={handleSubmit} autoComplete={'off'}>
          {isEmailMethod ? (
            <Input
              type={'email'}
              label={i18n.getMessage('login.label.email')}
              placeholder={i18n.getMessage('login.placeholder.email')}
              name={'email'}
              value={values.email}
              onChange={handleEmailChange}
              onBlur={validateOnBlur('email')}
              initialStatus={submitCount}
              // Not "email": Chrome's credential manager ignores a plain "off" on login-shaped
              // forms and will still offer/fill whatever it last saved as the "username" for this
              // origin — including a phone number saved from a previous Phone-tab login, into this
              // Email field, on tab switch. "new-password" is a widely-used, reliable trick that
              // Chrome treats as "not a field to autofill", regardless of the input's actual type.
              autoComplete={'new-password'}
              // Not on touch devices: it would open the keyboard over the page on load.
              autoFocus={shouldAutoFocus()}
              isDisabled={isLoading}
              error={
                errors?.email ||
                (visibleStoreError?.fields && i18n.getMessage(`error.${visibleStoreError.fields.email}`))
              }
              isApiError={!!visibleStoreError}
            />
          ) : (
            <Input
              type={'phone'}
              label={i18n.getMessage('register.label.phone')}
              name={'phone'}
              value={values.phone}
              setValue={setFieldValue}
              onChange={handleChange}
              onBlur={validateOnBlur('phone')}
              setCountryCode={setCountryCode}
              initialStatus={submitCount}
              // See the email field's comment above — same reasoning, same fix.
              autoComplete={'new-password'}
              // Not on touch devices: it would open the keyboard over the page on load.
              autoFocus={shouldAutoFocus()}
              isDisabled={isLoading}
              error={errors?.phone || (errors && prepareFieldErrors(i18n, errors)?.phone)}
              isApiError={!!visibleStoreError}
            />
          )}

          <div className={'login-form__password'}>
            <Input
              type={'password'}
              label={i18n.getMessage('login.label.password')}
              name={'password'}
              placeholder={i18n.getMessage('login.placeholder.password')}
              value={values.password}
              initialStatus={submitCount}
              onChange={handleChange}
              onBlur={validateOnBlur('password')}
              // Not "current-password": that's the strongest possible hint for Chrome to offer/fill
              // a remembered password, which is exactly what carries a stale Phone-tab credential
              // into this field on tab switch. Same fix as the identifier field above.
              autoComplete={'new-password'}
              isDisabled={isLoading}
              error={errors?.password || (visibleStoreError?.fields && visibleStoreError.fields.password)}
              isApiError={!!visibleStoreError}
              // Rendered inside the field's own under-text row (next to the error message) instead
              // of as a sibling below it, specifically so it can't be overlapped by a long,
              // multi-line password error — see Login.scss's .login-form__password overrides.
              underTextAction={
                <a href={ROUTE_PATHS.PASSWORD_RECOVERY} className={'login-form__forgot-password'}>
                  {i18n.getMessage('login.btn.forgot')}
                </a>
              }
            />
          </div>

          {/*
          Per CLAUDE.md (disable in-flight-request buttons): isDisabled is driven by isLoading, not just
          form validity, so the request cannot be fired twice by repeat clicks while it's in flight.
        */}
          <Button
            type={'primary'}
            roleType={'submit'}
            size={'large'}
            fullWidth={true}
            onClick={() => {}}
            isDisabled={isLoading}
          >
            {isLoading ? <Loader /> : i18n.getMessage('login.btn.login')}
          </Button>

          <div className={'login-form__divider'}>
            <span />
            <p>{i18n.getMessage('login.divider.or')}</p>
            <span />
          </div>

          <div className={'login-form__social'}>
            {/*
            UI-only per TRN-6921 spec §7.5 — no PKCE / provider SDK wiring in this ticket, hence a plain
            disabled <button>, not a store-connected action. The Tooltip explains why to the user.
          */}
            <Tooltip
              content={i18n.getMessage('login.tooltip.socialComingSoon', { provider: 'Apple' })}
              direction={'top'}
            >
              <button type={'button'} className={'login-form__social-btn'} disabled>
                <AppleLogo />
                {i18n.getMessage('login.btn.loginWithApple')}
              </button>
            </Tooltip>
            <Tooltip
              content={i18n.getMessage('login.tooltip.socialComingSoon', { provider: 'Google' })}
              direction={'top'}
            >
              <button type={'button'} className={'login-form__social-btn'} disabled>
                <GoogleLogo />
                {i18n.getMessage('login.btn.loginWithGoogle')}
              </button>
            </Tooltip>
          </div>
        </form>

        <p className={'login-form__signup'}>
          {i18n.getMessage('login.noAccount')}{' '}
          <AuthPageLink to={ROUTE_PATHS.REGISTRATION} className={'login-form__signup-link'} isDisabled={isLoading}>
            {i18n.getMessage('login.link.signUp')}
          </AuthPageLink>
        </p>
      </div>

      <AuthMobileApp variant={AUTH_MOBILE_APP_VARIANTS.BADGES} />
    </section>
  );
};

Login.propTypes = {
  phone: PropTypes.string.isRequired,
  setPhone: PropTypes.func.isRequired,
  setUsername: PropTypes.func.isRequired,
  setPassword: PropTypes.func.isRequired,
  sendAuthorizationData: PropTypes.func.isRequired,
  error: PropTypes.any,
  isLoading: PropTypes.bool
};

export default observer(Login);
